// 抽礼物（EP-006-S3 / GDD 01 §②-③ + §9 反重复保护）。
//
// 纯函数，rng 必须注入（GDD 01 §2.5 验收前置）。本文件属 domain 层，**不得**直接调用
// Date.now / Math.random（CI 守门 check-no-tick-accumulator）；rolledAt 由调用方注入 `now`。
//
// 流程：选池 → 应用传说保护（tripCount < 10 传说清零 + 回填普通档）
//        → 应用反重复降权（窗口内累计 ≥ 阈值件 ×0.5，仅 N 档；GDD 01 §9）
//        → 抽件数（84/13/3）→ 每件独立抽样（受 sameGiftMaxPerTrip 上限约束）
//        → 返回 { giftIds, rolledAt, decayedGiftIds }（A1 红线：giftIds.length ≥ 1）。
//
// 备注：长旅行倾斜（稀有×1.5 / 传说×2）需要 isLongTrip 上下文，本批次 roll 签名未携带，
//       故长旅行倾斜延后实现；本批次断言套件使用常规旅行，结论不受影响（见 CHANGELOG）。
//
// 🆕 §9：签名扩展为 `roll(config, destination, tripCount, recentTripDrops, rng, now)`。
//   `recentTripDrops` 由调用方（planReturn / store.tick）从 save 透传，shape = string[][]（按趟分组）。
//   读取侧统一 `?? []`，缺省/字段缺失/空窗口均静默无副作用（B7 断言）。
import type { DestinationId } from '../../save/types';
import type { DropConfig } from './dropConfig';
import { FALLBACK_POOL } from './dropConfig';
import { GIFT_BY_ID } from '../gifts/giftDef';
import { weightedPick } from '../../core/weightedPick';

export interface RollResult {
  giftIds: string[];
  rolledAt: number;
  // 🆕 GDD 01 §9.7：本次被反重复降权命中并改重的 giftId 列表（去重）。
  //   埋点降级：playtest 期走 §⑧-D3 console 结构化日志；
  //   架构 owner 签收后可走 drop_rolled payload 的 decayedGiftIds 字段。
  //   无降权时 = []（不是 undefined），便于序列化与消费方做 .length 判定。
  decayedGiftIds: string[];
}

// 传说保护（GDD 01 §2.3）：保护期内传说权重清零，按同池普通档现有权重比例回填普通档，
// 少见/稀有档不变。返回新的权重表，不修改入参。
function applyLegendaryProtection(
  pool: Record<string, number>,
  tripCount: number,
  legendaryProtectionTrips: number,
): Record<string, number> {
  if (tripCount >= legendaryProtectionTrips) {
    return { ...pool };
  }

  let legendaryTotal = 0;
  let normalTotal = 0;
  for (const [id, w] of Object.entries(pool)) {
    const rarity = GIFT_BY_ID[id]?.rarity;
    if (rarity === 'L') legendaryTotal += w;
    else if (rarity === 'N') normalTotal += w;
  }
  if (legendaryTotal <= 0 || normalTotal <= 0) {
    // 无传说权重可清，或无可回填的普通档：直接清零传说档即可
    return Object.fromEntries(
      Object.entries(pool).map(([id, w]) => [id, GIFT_BY_ID[id]?.rarity === 'L' ? 0 : w]),
    );
  }

  const factor = 1 + legendaryTotal / normalTotal;
  const out: Record<string, number> = {};
  for (const [id, w] of Object.entries(pool)) {
    const rarity = GIFT_BY_ID[id]?.rarity;
    if (rarity === 'L') out[id] = 0;
    else if (rarity === 'N') out[id] = w * factor;
    else out[id] = w;
  }
  return out;
}

// 🆕 GDD 01 §9：反重复降权纯函数（已 export，供 antiRepeat.test.ts 白盒断言权重 / 命中列表）。
//
// 输入：传入传说保护处理后的池（已应用 §2.3）+ 最近 antiRepeatWindowSize 次带礼物的回归（按趟分组）。
// 输出：① 新池（不改入参，浅拷贝 + 个别 key 覆盖）；② 被降权命中且改重的 giftId 列表（去重）。
//
// 判定规则（§9.2 + §9.3）：
//   - 窗口按件计数（不是按趟），跨趟 flatten 后逐个 N 档 giftId 数累计件数；
//   - 仅 N 档参与判定（U/R/L 不降权），但 U/R/L 件仍占用窗口条目；
//   - count(id) >= antiRepeatTriggerCount 才命中 → 该件权重 ×antiRepeatDecayFactor。
//
// 优先级（§9.4）：传说保护 → 长旅行倾斜 → 反重复降权 —— 本函数接收的 pool 已是"传说保护后"，
//   所以 `pool` 中 N 件的权重已是回填比例后的值；在这里再对特定 N 件 ×decayFactor 不会
//   与传说保护的回填逻辑打架（两者作用对象 + 数值域不同；§9.4 三者互不重叠论证）。
export function applyAntiRepeatDecay(
  pool: Record<string, number>,
  recentTripDrops: readonly string[][],
  windowSize: number,
  triggerCount: number,
  decayFactor: number,
): { pool: Record<string, number>; decayedGiftIds: string[] } {
  // E1：窗口为空/缺省 → 全部不降权（机制静默通过；与首旅应有行为一致）。
  if (recentTripDrops.length === 0) {
    return { pool: { ...pool }, decayedGiftIds: [] };
  }

  // 取最近 windowSize 趟（窗口的"趟"边界必须保留，故 slice 顶层；§9.5 string[][] 选择依据）。
  const window = recentTripDrops.slice(-windowSize);

  // 按件计数（flatten）；U/R/L 件跳过判定但占用窗口条目（§9.6 E5）。
  const counts = new Map<string, number>();
  for (const trip of window) {
    for (const id of trip) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }

  const decayedGiftIds: string[] = [];
  const out: Record<string, number> = { ...pool };
  for (const [id, c] of counts) {
    if (c < triggerCount) continue; // 未达阈值
    const rarity = GIFT_BY_ID[id]?.rarity;
    if (rarity !== 'N') continue; // 仅 N 档参与降权（§9.2 维度二 / §9.6 E5）
    if (out[id] === undefined) continue; // 当前池不包含该 id（防御：池被替换过）
    // 命中 → 该件权重 × decayFactor（保留正值，归一化由 weightedPick 处理）。
    out[id] = out[id] * decayFactor;
    decayedGiftIds.push(id);
  }
  return { pool: out, decayedGiftIds };
}

function pickCount(config: DropConfig, rng: () => number): number {
  const ks = config.countWeights.map((c) => c.k);
  const ws = config.countWeights.map((c) => c.w);
  return weightedPick(ks, ws, rng);
}

export function roll(
  config: DropConfig,
  destination: DestinationId,
  tripCount: number,
  recentTripDrops: readonly string[][],
  rng: () => number,
  now: number,
): RollResult {
  const basePool = config.pools[destination] ?? FALLBACK_POOL;
  const protectedPool = applyLegendaryProtection(basePool, tripCount, config.tuning.legendaryProtectionTrips);
  // §9.4 优先级：传说保护 → 反重复降权（长旅行倾斜未实现，本批次不参与）。
  const { pool: decayedPool, decayedGiftIds } = applyAntiRepeatDecay(
    protectedPool,
    recentTripDrops,
    config.tuning.antiRepeatWindowSize,
    config.tuning.antiRepeatTriggerCount,
    config.tuning.antiRepeatDecayFactor,
  );

  const ids = Object.keys(decayedPool).filter((id) => (decayedPool[id] ?? 0) > 0);
  const weights = ids.map((id) => decayedPool[id] as number);
  if (ids.length === 0) {
    throw new Error(`roll: 目的地 ${destination} 池为空，且兜底池不可用`);
  }

  const count = pickCount(config, rng);
  const seen = new Map<string, number>();
  const giftIds: string[] = [];
  const maxPer = config.tuning.sameGiftMaxPerTrip;

  for (let i = 0; i < count; i++) {
    let chosen = '';
    // 同物上限：达到上限则重抽（最多 10 次），仍超限则接受（优雅降级，绝不返回空数组）。
    for (let attempt = 0; attempt < 10; attempt++) {
      chosen = weightedPick(ids, weights, rng);
      const c = seen.get(chosen) ?? 0;
      if (c + 1 <= maxPer) break;
    }
    seen.set(chosen, (seen.get(chosen) ?? 0) + 1);
    giftIds.push(chosen);
  }

  if (giftIds.length === 0) {
    // 设计红线（GDD 01 A1）：永不空手。理论上不可达，作为最后防线再抽一次。
    giftIds.push(weightedPick(ids, weights, rng));
  }

  return { giftIds, rolledAt: now, decayedGiftIds };
}
